import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DateInput } from "@/components/DateInput";

const METHODS = {
  numerario: ["Numerário", []], letra: ["Letra", ["entity"]], cheque: ["Cheque", ["entity", "cheques"]],
  transferencia: ["Transferência bancária", ["iban_from", "iban_to"]], cartao: ["Cartão de pagamento", ["iban_from", "iban_to"]],
  outro: ["Outro meio", ["other"]],
};
const LBL = { count: "N.º de pagamentos", value: "Valor (€)", entity: "Entidade sacada", cheques: "N.º(s) do(s) cheque(s)", iban_from: "IBAN do ordenante", iban_to: "IBAN do beneficiário", other: "Qual?" };
const REP = [["nome", "Nome do representante"], ["titulo", "Título representativo (ex.: procuração)"], ["nif", "NIF"], ["doc_tipo", "Documento (CC, passaporte…)"], ["doc_num", "N.º do documento"], ["doc_validade", "Validade"]];
const field = "text-[12px] text-slate-500";

function Payment({ p, onChange, onRemove }) {
  const [label, extra] = METHODS[p.method];
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`impic-pay-${p.method}`}>
      <div className="flex items-center justify-between mb-2"><b className="text-sm">{label}</b>
        <button onClick={onRemove} className="text-slate-400 hover:text-red-600" aria-label="Remover" data-testid={`impic-pay-remove-${p.method}`}><X size={16} /></button></div>
      <div className="grid grid-cols-2 gap-2">
        {["count", "value", ...extra].map((k) => (
          <label key={k} className={k === "other" || k === "cheques" ? "col-span-2" : ""}><span className={field}>{LBL[k]}</span>
            <Input value={p[k] || ""} inputMode={k === "count" || k === "value" ? "decimal" : undefined} onChange={(e) => onChange({ ...p, [k]: e.target.value })} data-testid={`impic-pay-${p.method}-${k}`} /></label>
        ))}
      </div>
    </div>
  );
}

export function Rep({ title, rep, onChange, tid }) {
  return (
    <div className="space-y-2" data-testid={tid}>
      <b className="text-sm">{title}</b>
      <div className="grid grid-cols-2 gap-2">
        {REP.map(([k, l]) => (
          <label key={k} className={k === "nome" || k === "titulo" ? "col-span-2" : ""}><span className={field}>{l}</span>
            {k === "doc_validade" ? <DateInput value={rep[k] || ""} onChange={(e) => onChange({ ...rep, [k]: e.target.value })} data-testid={`${tid}-${k}`} />
              : <Input value={rep[k] || ""} onChange={(e) => onChange({ ...rep, [k]: e.target.value })} data-testid={`${tid}-${k}`} />}</label>
        ))}
      </div>
    </div>
  );
}

export function PaymentEditor({ pays, setPays, price }) {
  const add = (m) => setPays([...pays, { method: m, count: "1", value: pays.length === 0 && price ? String(price) : "" }]);
  const left = Object.keys(METHODS).filter((m) => !pays.some((p) => p.method === m));
  return (
    <div className="space-y-3" data-testid="payment-editor">
      {pays.map((p, i) => <Payment key={p.method} p={p} onChange={(np) => setPays(pays.map((x, j) => (j === i ? np : x)))} onRemove={() => setPays(pays.filter((_, j) => j !== i))} />)}
      <div className="flex flex-wrap gap-1.5">
        {left.map((m) => <button key={m} type="button" onClick={() => add(m)} className="inline-flex items-center gap-1 text-[13px] px-2.5 py-1.5 rounded-full border border-slate-300 hover:bg-slate-50" data-testid={`impic-pay-add-${m}`}><Plus size={13} />{METHODS[m][0]}</button>)}
      </div>
    </div>
  );
}

export function ImpicDados({ row, onClose, onSaved }) {
  const [pays, setPays] = useState(row.impic.payments || []);
  const [repS, setRepS] = useState(row.impic.rep_seller || {});
  const [repB, setRepB] = useState(row.impic.rep_buyer || {});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/impic/processes/${row.id}`, { payments: pays, rep_seller: row.has_procuracao ? repS : null, rep_buyer: row.buyer_procuracao ? repB : null });
      toast.success("Dados guardados");
      onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="impic-dados-dialog">
        <DialogHeader>
          <DialogTitle>{row.ref} · dados da comunicação</DialogTitle>
          <DialogDescription>Preencha com base na escritura{row.has_procuracao || row.buyer_procuracao ? " e na procuração" : ""}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <b className="text-sm">Meio(s) de pagamento</b>
          <PaymentEditor pays={pays} setPays={setPays} price={row.price} />
          {row.has_procuracao && <Rep title="Representante do vendedor" rep={repS} onChange={setRepS} tid="impic-rep-seller" />}
          {row.buyer_procuracao && <Rep title="Representante do comprador" rep={repB} onChange={setRepB} tid="impic-rep-buyer" />}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm border border-slate-300" data-testid="impic-dados-cancel">Cancelar</button>
          <button disabled={saving} onClick={save} className="px-4 py-2.5 rounded-lg text-sm font-semibold navy-bg text-white disabled:opacity-40" data-testid="impic-dados-save">Guardar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
